import * as React from "react";
import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";
import { ClipboardTextIcon } from "@phosphor-icons/react/dist/ssr/ClipboardText";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { CampaignRequestListRow } from "@/hooks/api/use-campaign-requests";
import { campaignRequestsKeys } from "@/hooks/api/use-campaign-requests";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { CustomTable } from "@/components/customTable";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { BlockerChips, HelpTooltip, NOT_METERED_HELP, StateChip } from "./components";
import {
	formatDateRange,
	formatMoney,
	marketFlag,
	MARKETS,
	REQUEST_OBJECTIVE_OPTIONS,
	REQUEST_STATE_CLIENT_LABELS,
} from "./constants";
import { LAST_STEP, progressOfRow, resumeStep, stepLabel } from "./progress";

/**
 * The client's list of campaign requests — UX §02, "the entry point".
 *
 * The column that matters is "Waiting on". It answers the only question a client actually has and
 * it kills the "where are we on this?" email, so it is blank whenever the ball is ours and there
 * is nothing they could do about it.
 *
 * Drafts are first-class rows rather than invisible clutter: an abandoned wizard is recoverable,
 * clicking one reopens it where it stopped, and the row says which step that was.
 *
 * Every column here is drawn from the row itself. The dial window and the committed total used to
 * be topped up per row from `GET /campaign-requests/:id` — up to twenty-five of those on first
 * paint, eight child queries each, to fill in two columns that then read "—" from row twenty-six
 * onwards. They are on the list resource now, built for the whole page in five queries, so the
 * cap and the dash it produced are both gone.
 */

/**
 * The status filter reads in the client's language, not the state machine's — the list says
 * "Awaiting your confirmation", so the filter beside it has to say the same thing.
 */
const CLIENT_STATE_OPTIONS = Object.entries(REQUEST_STATE_CLIENT_LABELS).map(([value, label]) => ({ value, label }));

/** Where a draft row goes: back into the wizard, at the first step still owing work. */
function resumeHref(request: CampaignRequestListRow): string {
	const step = resumeStep(progressOfRow(request));
	return `/campaign-requests/create?request=${request.id}&step=${step}`;
}

/**
 * The line under the title. A live request shows the client's own project code, which is what
 * lands on their invoice; a draft shows how far through the wizard it got instead, so an
 * abandoned one is visibly recoverable rather than just untitled.
 */
function secondaryLine(request: CampaignRequestListRow): string {
	if (request.state !== "draft") return request.clientReference ?? "—";

	const step = resumeStep(progressOfRow(request));
	return `Step ${step} of ${LAST_STEP} · ${stepLabel(step)}`;
}

function EmptyState({ onCreate }: { onCreate: () => void }): React.JSX.Element {
	return (
		<Card variant="outlined" sx={{ p: 6, textAlign: "center" }}>
			<Stack spacing={2} sx={{ alignItems: "center" }}>
				<ClipboardTextIcon size={40} color="var(--mui-palette-text-secondary)" />

				<Box>
					<Typography variant="h6">No campaign requests yet</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mt: 0.5 }}>
						Tell us what you want called and in which markets, and we price it before anything dials. You can save and
						come back at any point — a half-finished request stays on this list.
					</Typography>
				</Box>

				<Button startIcon={<PlusIcon />} variant="contained" onClick={onCreate}>
					New Request
				</Button>
			</Stack>
		</Card>
	);
}

export function CampaignRequests(): React.JSX.Element {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const {
		items: requests,
		isLoading,
		totalItems,
		refetchData: fetchRequests,
	} = useCachedTableStateWithUrl<CampaignRequestListRow>({
		queryKey: campaignRequestsKeys.lists(),
		endpoint: "/campaign-requests",
	});

	const filtersOptions: FilterOption[] = React.useMemo(
		() => [
			{ name: "search", label: "Search", value: "", type: FilterType.TEXT },
			{
				name: "state",
				label: "Status",
				value: [],
				type: FilterType.MULTI_SELECT,
				options: CLIENT_STATE_OPTIONS,
			},
			{
				name: "market",
				label: "Market",
				value: [],
				type: FilterType.MULTI_SELECT,
				options: MARKETS.map((market) => ({ label: `${market.flag} ${market.label}`, value: market.value })),
			},
			{
				name: "objective",
				label: "Objective",
				value: [],
				type: FilterType.MULTI_SELECT,
				options: REQUEST_OBJECTIVE_OPTIONS,
			},
		],
		[]
	);

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: { id: "title", label: "Campaign", align: "left", sortBy: "title", sx: { fontWeight: "600" } },
				accessorKey: "title",
				getCellValue: (row: CampaignRequestListRow) => (
					<Box>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{row.title || "Untitled campaign"}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{secondaryLine(row)}
						</Typography>
					</Box>
				),
			},
			{
				header: { id: "markets", label: "Markets", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "markets",
				getCellValue: (row: CampaignRequestListRow) =>
					row.markets.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							—
						</Typography>
					) : (
						<Typography variant="body2">
							{row.markets.map((market) => `${marketFlag(market)} ${market}`.trim()).join(" · ")}
						</Typography>
					),
			},
			{
				header: { id: "state", label: "Status", align: "left", sortBy: "state", sx: { fontWeight: "600" } },
				accessorKey: "state",
				getCellValue: (row: CampaignRequestListRow) => <StateChip state={row.state} variant="client" />,
			},
			{
				// The most important column on the screen. Blank when it is on us.
				header: { id: "blockers", label: "Waiting On", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "blockers",
				getCellValue: (row: CampaignRequestListRow) =>
					row.state === "draft" ? (
						<HelpTooltip title="This request has not been sent to us yet. Pick it up where you left off.">
							<Chip label="You · resume" size="small" variant="outlined" />
						</HelpTooltip>
					) : (
						<BlockerChips blockers={row.blockers} />
					),
			},
			{
				header: { id: "dates", label: "Dates", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "dates",
				getCellValue: (row: CampaignRequestListRow) => (
					<Typography variant="body2">{formatDateRange(row.summary.dialStart, row.summary.dialEnd)}</Typography>
				),
			},
			{
				/*
				 * "Set-up — committed", not "cost to date". The column used to be headed cost to date and
				 * to show the confirmed set-up total, which reads as everything this campaign has cost so
				 * far and is not: call time is not metered anywhere (see `MeteringGap`), so the figure
				 * silently understated a running campaign by whatever it had dialled. The heading now
				 * names the one number underneath it, and a request with nothing committed says so
				 * rather than showing a confident S$ 0.00.
				 */
				header: { id: "cost", label: "Setup — Committed", align: "right", sx: { fontWeight: "600" } },
				accessorKey: "cost",
				getCellValue: (row: CampaignRequestListRow) => {
					const { committedTotal, currency } = row.summary;

					return (
						<HelpTooltip
							title={
								committedTotal
									? `Setup charges committed when the quote was confirmed. ${NOT_METERED_HELP}`
									: `Nothing is committed until a quote is confirmed. ${NOT_METERED_HELP}`
							}
						>
							<Typography
								variant="body2"
								color={committedTotal ? "text.primary" : "text.secondary"}
								sx={{ cursor: "help", fontWeight: committedTotal ? 600 : 400, textAlign: "right" }}
							>
								{committedTotal ? formatMoney(committedTotal, currency) : "Not committed"}
							</Typography>
						</HelpTooltip>
					);
				},
			},
		];
	}, []);

	const hasFilters = ["search", "state", "market", "objective"].some((key) => searchParams.getAll(key).length > 0);
	const showEmptyState = !isLoading && totalItems === 0 && !hasFilters;

	const handleRowClick = (row: CampaignRequestListRow): void => {
		navigate(row.state === "draft" ? resumeHref(row) : `/campaign-requests/${row.id}`);
	};

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={3}
					sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
				>
					<Box sx={{ flex: "1 1 auto" }}>
						<Typography variant="h4">Campaign Requests</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							Your campaigns and what each one is waiting on
						</Typography>
					</Box>

					<Button startIcon={<PlusIcon />} variant="contained" onClick={() => navigate("/campaign-requests/create")}>
						New Request
					</Button>
				</Stack>

				{showEmptyState ? (
					<EmptyState onCreate={() => navigate("/campaign-requests/create")} />
				) : (
					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={requests}
								refetchData={fetchRequests}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
								onRowClick={handleRowClick}
							/>
						</Card>
					</TableAnimation>
				)}
			</Stack>
		</Box>
	);
}
